import { SSN_REGEX, US_COUNTRY_CODE, US_PHONE_DIGIT_LENGTH } from "@constants";
import { Currency, FormatChangeProps, FormatCurrentInputProps, GetCurrencyProps } from "@ctypes";

export const formatPhoneNumber = (value: string) => {
  const sanitizedValue = value.replace(/^(\+)?1/, "").replace(/\D/g, "");

  let formattedValue = sanitizedValue;

  if (sanitizedValue.length > 3 && sanitizedValue.length < 7) {
    formattedValue = `(${sanitizedValue.substring(0, 3)}) ${sanitizedValue.substring(3)}`;
  } else if (sanitizedValue.length >= 7) {
    formattedValue = `(${sanitizedValue.substring(
      0,
      3,
    )}) ${sanitizedValue.substring(3, 6)}-${sanitizedValue.substring(6)}`;
  }

  return formattedValue;
};

export const formatDateOfBirth = (value: string) => {
  const sanitizedValue = value.replace(/[^\d]/g, "");

  let formattedValue = sanitizedValue;

  if (sanitizedValue.length > 4 && sanitizedValue.length < 7) {
    formattedValue = sanitizedValue.substring(0, 4) + "-" + sanitizedValue.substring(4);
  } else if (sanitizedValue.length >= 7) {
    formattedValue =
      sanitizedValue.substring(0, 4) +
      "-" +
      sanitizedValue.substring(4, 6) +
      "-" +
      sanitizedValue.substring(6, 8);
  }

  return formattedValue;
};

export const sanitizePhoneNumber = (value: string) => {
  return value.replace(/\D/g, "");
};

/**
 * Formats a US phone number as E.164 (+14155551234), which is what the ad
 * platforms hash against. Returns undefined for anything that is not a
 * recognisable US number: a malformed value would not match anyone and would
 * quietly drag down match rates, so it is better to send nothing.
 */
export const toE164 = (value?: string | null) => {
  const digits = value?.replace(/\D/g, "") ?? "";

  if (digits.length === US_PHONE_DIGIT_LENGTH) {
    return `+${US_COUNTRY_CODE}${digits}`;
  }

  if (digits.length === US_PHONE_DIGIT_LENGTH + 1 && digits.startsWith(US_COUNTRY_CODE)) {
    return `+${digits}`;
  }

  return undefined;
};

export const formatSsnNumber = (value: string) => {
  const sanitizedValue = value.replace(/[^0-9]/g, "");

  let formattedValue = sanitizedValue;

  if (sanitizedValue.length > 3 && sanitizedValue.length <= 5) {
    formattedValue = sanitizedValue.substring(0, 3) + "-" + sanitizedValue.substring(3);
  } else if (sanitizedValue.length > 5) {
    formattedValue =
      sanitizedValue.substring(0, 3) +
      "-" +
      sanitizedValue.substring(3, 5) +
      "-" +
      sanitizedValue.substring(5);
  }

  return formattedValue;
};

export function formatCurrency(value: number | null | undefined, decimals?: boolean) {
  const n = typeof value === "number" && !Number.isNaN(value) ? value : 0;
  const stringValue = decimals ? n.toFixed(2) : n.toFixed(0);

  const [integerPart, decimalPart] = stringValue.split(".");

  const integerWithCommas = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  const formattedValue =
    decimals && decimalPart ? `${integerWithCommas}.${decimalPart}` : integerWithCommas;

  return `$${formattedValue}`;
}

export const formatCurrencyShorthand = (value: number | null | undefined, currency?: Currency) => {
  const amount = typeof value === "number" && !Number.isNaN(value) ? value : 0;
  const symbol = getCurrencySymbol({ currency });
  const absoluteAmount = Math.abs(amount);
  const sign = amount < 0 ? "-" : "";

  if (absoluteAmount < 1_000) return formatCurrency(amount);

  const truncate = (quotient: number) =>
    Number.isInteger(quotient) ? `${quotient}` : `${Math.floor(quotient * 10) / 10}`;

  if (absoluteAmount >= 1_000_000) {
    return `${sign}${symbol}${truncate(absoluteAmount / 1_000_000)}M`;
  }

  return `${sign}${symbol}${truncate(absoluteAmount / 1_000)}K`;
};

export const getPossessiveName = (name: string) => {
  const trimmedName = name.trim();

  if (!trimmedName) return trimmedName;

  return /s$/i.test(trimmedName) ? `${trimmedName}'` : `${trimmedName}'s`;
};

export const getCurrencySymbol = ({ currency = Currency.USD }: GetCurrencyProps = {}) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).format(0)[0];
};

export const formatCurrencyInput = ({ input, decimals, currency }: FormatCurrentInputProps) => {
  let amount = input.replace(/,/g, "");

  amount = amount.replace(/[^0-9.]/g, "");

  if (amount.length > 1) {
    amount = amount.replace(/^0+/, "");
  }

  if (decimals) {
    if (amount.endsWith(".")) {
      const integerPart = amount.slice(0, -1).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      return `${getCurrencySymbol({ currency })}${integerPart}.`;
    }

    const parts = amount.split(".");
    if (parts.length > 1) {
      const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      const decimalPart = parts[1].slice(0, 2);
      return `${getCurrencySymbol({ currency })}${integerPart}.${decimalPart}`;
    }
  } else {
    amount = amount.replace(/\./g, "");
  }

  if (amount.length > 0) {
    return `${getCurrencySymbol({ currency })}${amount.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  }

  return "";
};

export const convertCentsToDollar = (value?: number | null) => {
  if (!value) return 0;

  return value / 100;
};

export const convertDollarToCents = (value?: number | null) => {
  if (!value) return 0;

  return Math.round(value * 100);
};

export const convertToMultiplier = (percentage: number) => {
  return percentage / 100 + 1;
};

export const formatMaskedSsnNumber = (value: string) => {
  if (value.length === 9) {
    return `${value.substring(0, 3)}-${value.substring(3, 5)}-${value.substring(5)}`;
  }
  return value;
};

export const formatGrossReturns = ({ difference, percentage }: FormatChangeProps) => {
  let sign = "";

  if (difference > 0) {
    sign = "+";
  } else if (difference < 0) {
    sign = "-";
  }

  const formattedDifference = formatCurrency(Math.abs(difference), true);

  const formattedPercentage = Math.abs(percentage).toFixed(2);

  return `${sign}${formattedDifference} (${formattedPercentage}%)`;
};

export const formatSign = (value: number) => {
  let sign = "";

  if (value > 0) {
    sign = "+";
  } else if (value < 0) {
    sign = "-";
  }

  return sign;
};

export const formatPercentage = (value: number) => {
  return `${value.toFixed(2)}%`;
};

export const maskSsnNumber = (ssn: string) => {
  if (!SSN_REGEX.test(ssn)) {
    return ssn;
  }

  return `***-**-${ssn.slice(-4)}`;
};

export const formatErrorMessage = (error?: string) => {
  if (!error) return undefined;
  return error.replace(/^\[[a-zA-Z0-9]+\]:?\s*/, "").trim();
};
